import AdminPermission from "components/admin/AdminPermission";
import {
  httpCancelSession,
  httpFetchActiveSessions,
  httpFetchCancelledSessions,
  httpFetchInactiveSessions,
} from "api/admin/bitoshi-hour";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import ConfirmModal from "components/ui/ConfirmModal";
import VButton from "components/ui/VButton";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

type InactiveCategory = "upcoming" | "past" | "cancelled";

const COUNTRY_FLAGS: Record<string, string> = {
  GH: "🇬🇭",
  NG: "🇳🇬",
  KE: "🇰🇪",
};

interface Session {
  id: number;
  name: string;
  isCancelled: boolean;
  startDateTime: string;
  endDateTime: string;
  data: string;
  eligibleCountries: string[];
  createdAt: string;
  updatedAt: string;
}

interface SessionCardProps {
  session: Session;
}

const SessionCard: React.FC<SessionCardProps> = ({ session }) => {
  const sessionData = JSON.parse(session.data);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isPastEvent = new Date(session.endDateTime) < new Date();
  const isCancelled = session.isCancelled;
  const [showCancelModal, setShowCancelModal] = useState(false);

  const cancelMutation = useMutation(() => httpCancelSession(session.id), {
    onSuccess: () => {
      toast.success("Session cancelled successfully");
      setShowCancelModal(false);
      queryClient.invalidateQueries(["activeSessions"]);
      queryClient.invalidateQueries(["inactiveSessions"]);
      queryClient.invalidateQueries(["cancelledSessions"]);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? "Failed to cancel session");
      setShowCancelModal(false);
    },
  });

  return (
    <div>
      <div className="bg-gray-100 p-6 rounded-xl inline-flex flex-col gap-6">
        <h4 className="text-xl font-">{session.name}</h4>
        <div className="flex gap-8">
          <p>
            From:{" "}
            <span className="ml-2 bg-white py-2 px-3 rounded-lg">
              {new Date(session.startDateTime).toDateString()},{" "}
              {new Date(session.startDateTime).toLocaleTimeString()}
            </span>{" "}
          </p>
          <p>
            To:{" "}
            <span className="ml-2 bg-white py-2 px-3 rounded-lg">
              {new Date(session.endDateTime).toDateString()},{" "}
              {new Date(session.endDateTime).toLocaleTimeString()}
            </span>{" "}
          </p>
        </div>
        <div className="flex gap-8">
          <p>
            Buy:{" "}
            <span className="ml-2 bg-white py-2 px-3 rounded-lg">
              ₦ {sessionData.rate.buy.usd}
            </span>{" "}
          </p>
          <p>
            Sell:{" "}
            <span className="ml-2 bg-white py-2 px-3 rounded-lg">
              ₦ {sessionData.rate.sell.usd}
            </span>{" "}
          </p>
        </div>
        <p>
          Eligible Countries:{" "}
          <span className="ml-2 bg-white py-2 px-3 rounded-lg">
            {session.eligibleCountries
              .map((code) => COUNTRY_FLAGS[code] || code)
              .join(" ")}
          </span>{" "}
        </p>
        {!isPastEvent && !isCancelled && (
          <div className="flex gap-4">
            <AdminPermission permission={"bitoshiHour.sessions.edit"}>
              <VButton
                onClick={() => navigate(`/bitoshi-hour/${session.id}/edit`)}
                className="py-2 px-8 mt-2 text-white"
              >
                Edit Session
              </VButton>
            </AdminPermission>
            <AdminPermission permission={"bitoshiHour.sessions.edit"}>
              <VButton
                onClick={() => setShowCancelModal(true)}
                className="py-2 px-8 mt-2 text-white bg-red-500"
              >
                Cancel Session
              </VButton>
            </AdminPermission>
          </div>
        )}
      </div>
      <ConfirmModal
        isOpen={showCancelModal}
        close={() => setShowCancelModal(false)}
        title="Cancel Session"
        message="Are you sure you want to cancel this Bitoshi Hour session? This action cannot be undone."
        onConfirm={() => cancelMutation.mutate()}
        confirmText="Yes, Cancel"
        status={cancelMutation.isLoading ? "pending" : ""}
      />
    </div>
  );
};

const BitoshiHourHome: React.FC = () => {
  const [activeTab, setActiveTab] = useState<InactiveCategory>("upcoming");
  const navigate = useNavigate();

  const { data: activeSessions = [] } = useQuery(
    "activeSessions",
    httpFetchActiveSessions,
    {
      initialData: [],
      refetchInterval: 60 * 1000,
    },
  );

  const { data: inactiveSessions = [] } = useQuery(
    "inactiveSessions",
    httpFetchInactiveSessions,
    {
      initialData: [],
      refetchInterval: 60 * 1000,
    },
  );

  console.log("Inactive sessions: ", inactiveSessions);

  const { data: cancelledSessions = [] } = useQuery(
    "cancelledSessions",
    httpFetchCancelledSessions,
    {
      initialData: [],
      refetchInterval: 60 * 1000,
    },
  );

  const now = new Date();
  const upcomingSessions = inactiveSessions.filter(
    (session: Session) => new Date(session.startDateTime) > now,
  );
  console.log("Upcoming sessions: ", upcomingSessions);
  const pastSessions = inactiveSessions.filter(
    (session: Session) => new Date(session.endDateTime) < now,
  );

  interface ITabButtonProps {
    children: React.ReactNode;
    name: InactiveCategory;
  }

  function TabButton({ children, name }: ITabButtonProps) {
    const isActive = name === activeTab;
    const handleTabClick = () => {
      setActiveTab(name);
    };
    return (
      <button
        onClick={handleTabClick}
        className={`bg-gray-50 py-4 px-8 w-full ${isActive ? "border-b-4 border-gray-600" : ""}`}
      >
        {children}
      </button>
    );
  }

  const tabContent = {
    upcoming:
      upcomingSessions.length === 0 ? (
        <div className="py-4 text-gray-800">
          <p>No upcoming sessions</p>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {upcomingSessions.map((session: Session) => (
            <SessionCard key={session.id} session={session} />
          ))}
        </div>
      ),
    past:
      pastSessions.length === 0 ? (
        <div className="py-4 text-gray-800">
          <p>No past sessions</p>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {pastSessions.map((session: Session) => (
            <SessionCard key={session.id} session={session} />
          ))}
        </div>
      ),
    cancelled:
      cancelledSessions.length === 0 ? (
        <div className="py-4 text-gray-800">
          <p>No cancelled sessions</p>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {cancelledSessions.map((session: Session) => (
            <SessionCard key={session.id} session={session} />
          ))}
        </div>
      ),
  };

  const handleScheduleBtnClick = () => {
    navigate("/bitoshi-hour/create");
  };

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-5 text-left max-w-[1000px]">
        <div className="flex justify-between items-center mb-10">
          <h2 className="text-2xl">Bitoshi Hour</h2>
          <AdminPermission permission={"bitoshiHour.sessions.create"}>
            <VButton
              onClick={handleScheduleBtnClick}
              className="px-20 py-3 font-light text-white bg-black rounded-lg w-[200px] flex justify-between items-center"
            >
              Schedule Session
              <i className="fa-solid fa-plus"></i>
            </VButton>
          </AdminPermission>
        </div>
        <div className="mb-10">
          <h2 className="text-xl text-gray-400 mb-4">Active Sessions</h2>
          {activeSessions.length === 0 ? (
            <div className="py-4 text-gray-800">
              <p>No active sessions</p>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {activeSessions.map((session: Session) => (
                <SessionCard key={session.id} session={session} />
              ))}
            </div>
          )}
        </div>

        <div>
          <h2 className="text-xl text-gray-400 mb-4">Inactive Sessions</h2>
          <div className="flex gap-2 w-full mb-8">
            <TabButton name="upcoming">
              Upcoming ({upcomingSessions.length})
            </TabButton>
            <TabButton name="past">Past ({pastSessions.length})</TabButton>
            <TabButton name="cancelled">
              Cancelled ({cancelledSessions.length})
            </TabButton>
          </div>
          <div>{tabContent[activeTab]}</div>
        </div>
      </Main>
    </Container>
  );
};
export default BitoshiHourHome;
